<!-- =========================================================================================
File Name: TableDefault.vue
Description: Rendering default table
----------------------------------------------------------------------------------------
Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
Author: Pixinvent
Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<vx-card no-shadow>
		<configuration-navbar :handleOption="handleOption" :selected="schemaLocal" />
		<vx-card no-shadow>
			<label class="vs-input--label">{{ $t("DesignConfig.TypeConfigConfigName") }}:</label>
			<vs-input
				v-show="schemaLocal.id === 0 || changeName"
				:disabled="false"
				size="large"
				v-model.trim="schemaLocal.name"
				class="w-full mb-base"
			/>
			<configuration-list-item
				@handleSelectedEmit="handleSelected"
				@handleChangesEmit="handleChangesEmit"
				@saveEmit="save"
				:schema="schemaLocal"
				:addTrigger="addTrigger"
				:saveTrigger="saveTrigger"
				class="mb-base"
			/>
		</vx-card>
	</vx-card>
</template>

<script>
// Vue components
import ConfigurationNavbar from "./ConfigurationNavbar.vue";
import ConfigurationListItem from "./ConfigurationListItem.vue";
// Models
import TypeConfiguration from "../../models/TypeConfiguration";
// Store
import moduleTypeConfigCrud from "@/store/type-config-crud/moduleTypeConfigCrud.js";
import moduleCommon from "@/store/common/moduleCommon.js";
import moduleSchedule from "@/store/schedule/moduleSchedule.js";

export default {
	props: {
		schema: {
			type: Object,
			required: false,
		},
		additionalFields: {
			type: Boolean,
		},
		showEditIcon: {
			type: Boolean,
		},
		defaultAction: {
			type: String,
		},
		defaultTipology: {
			type: String,
		},
	},
	components: {
		ConfigurationNavbar,
		ConfigurationListItem,
	},
	data() {
		return {
			selected: {},
			schemaLocal: JSON.parse(JSON.stringify(this.schema)),
			addTrigger: 0,
			saveTrigger: 0,
			activeStatus: 0,
			changeName: false,
		};
	},
	methods: {
		handleChangesEmit(data) {},
		handleSelected(val) {
			this.selected = val;
		},
		handleOption(option) {
			switch (option) {
				case "SAVE":
					this.saveTrigger += 1;
					break;
				case "ADD_PROPERTY":
					this.addTrigger += 1;
					break;
				case "CHANGE_NAME":
					this.changeName = true;
					break;
				case "DELETE":
					this.confirmDeleteField(this.selected);
					break;
				case "CANCEL":
					this.$emit("changeActiveTabEmit", 0);
					break;
				case "CLEAR_SELECTION":
					this.selected = null;
					break;
				default:
					break;
			}
		},
		confirmDeleteField(field) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text:
					this.$t("DesignConfig.DeleteField.Text1") +
					` "${field.name}"? ` +
					this.$t("DesignConfig.DeleteField.Text2"),
				accept: () => {
					this.deleteRecord(field);
				},
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
			});
		},
		deleteRecord(field) {
			if (this.defaultAction === "DEFINITION") {
				this.deleteFromDefinition(field);
			}
			if (this.defaultAction === "LIST") {
				this.deleteFromStore(field);
			}
		},
		showDeleteSuccess() {
			this.$vs.notify({
				color: "success",
				title: this.$t("DesignConfig.UserDeleted.Title"),
				text: this.$t("DesignConfig.UserDeleted.Text"),
			});
		},
		save(properties) {
			// Removemos algunas propiedades segun el tipo
			const required = properties.filter((property) => property.required).map((property) => property.name);
			const reduceFormat = (accumulator, currentValue) => ({
				...accumulator,
				[currentValue.name]: this.formatProperty(currentValue),
			});
			const formattedProperties = properties.reduce(reduceFormat, {});

			if (this.schemaLocal.id > 0) this.updateConfigurationType(required, formattedProperties, properties);
			else this.createConfigurationType(required, formattedProperties, properties);
		},
		formatProperty(property) {
			const obj = {};
			const validateRequiredString = (required) => (required ? ["string"] : ["string", "null"]);
			const validateRequiredNum = (required) => (required ? ["integer"] : ["integer", "null"]);
			const validateRequiredBoolean = (required) => (required ? ["boolean"] : ["boolean", "null"]);
			const handleEnumAndType = (options, required) => {
				const transformedOptions = options.map((value) => (isNaN(value) ? value.trim() : +value));
				const types = Array.from(new Set(options.map((value) => (isNaN(value) ? "string" : "integer"))));
				return {
					enum: required ? transformedOptions : transformedOptions.concat(["", null]),
					type: required ? types : types.concat(["", null]),
				};
			};
			const handleAnyOf = (options_composed, required) => {
				return options_composed.map((opt_comp) => {
					if (opt_comp.type === "text") {
						const {minimum, maximum, values, ...res} = opt_comp;
						return {...res, type: validateRequiredString(required)};
					} else if (opt_comp.type === "number") {
						const {minLength, maxLength, values, ...res} = opt_comp;
						return {...res, type: validateRequiredNum(required)};
					} else {
						return {
							type: validateRequiredBoolean(required),
							enum: opt_comp.values && opt_comp.values.length <= 0 ? [true, false] : [...opt_comp.values],
						};
					}
				});
			};
			const propertyHandlers = new Map([
				[
					"select",
					() => {
						const {enum: enumValue, type} = handleEnumAndType(property.options, property.required);
						Object.assign(obj, {enum: enumValue, type});
					},
				],
				[
					"radio",
					() => {
						const {enum: enumValue, type} = handleEnumAndType(property.options, property.required);
						Object.assign(obj, {enum: enumValue, type});
					},
				],
				[
					"check",
					() => {
						const {enum: enumValue, type} = handleEnumAndType(property.options, false);
						Object.assign(obj, {
							minItems: property.required ? 1 : 0,
							type: "array",
							items: {enum: enumValue, type},
						});
					},
				],
				[
					"check_obj",
					() => {
						const {enum: enumValue, type} = handleEnumAndType(
							property.options_obj.map(({value}) => value),
							false
						);
						Object.assign(obj, {
							minItems: property.required ? 1 : 0,
							type: "array",
							items: {enum: enumValue, type},
						});
					},
				],
				[
					"hour",
					() => {
						Object.assign(obj, {
							minLength: property.minLength || 1,
							maxLength: property.maxLength || 250,
							type: validateRequiredString(property.required),
						});
					},
				],
				[
					"location",
					() => {
						Object.assign(obj, {
							minLength: property.minLength || 1,
							maxLength: property.maxLength || 250,
							type: validateRequiredString(property.required),
						});
					},
				],
				[
					"text",
					() => {
						Object.assign(obj, {
							minLength: property.minLength || 1,
							maxLength: property.maxLength || 250,
							type: validateRequiredString(property.required),
						});
					},
				],
				[
					"number",
					() => {
						Object.assign(obj, {
							type: validateRequiredNum(property.required),
							minimum: property.minimum || 1,
							maximum: property.maximum || 1000000,
						});
					},
				],
				[
					"email",
					() => {
						Object.assign(obj, {
							pattern:
								"^([a-z0-9-+*=&'/?^_}{~|`]+)(.?[a-z0-9-+*=&'/?^_}{~|`]+)*@[a-z0-9]+((-|.)[a-z0-9]+)*(.[a-z0-9]{1,20})$",
							type: validateRequiredString(property.required),
						});
					},
				],
				[
					"url",
					() => {
						Object.assign(obj, {format: "uri", type: validateRequiredString(property.required)});
					},
				],
				[
					"file",
					() => {
						Object.assign(obj, {format: "uri", type: validateRequiredString(property.required)});
					},
				],
				[
					"img",
					() => {
						Object.assign(obj, {format: "uri", type: validateRequiredString(property.required)});
					},
				],
				[
					"date",
					() => {
						Object.assign(obj, {format: "date", type: validateRequiredString(property.required)});
					},
				],
				[
					"boolean",
					() => {
						Object.assign(obj, {type: "boolean"});
					},
				],
				[
					"anyOf",
					() => {
						Object.assign(obj, {anyOf: handleAnyOf(property.options_composed, property.required)});
					},
				],
			]);
			const handler = propertyHandlers.get(property.type) || (() => {});
			handler();
			return obj;
		},
		async createConfigurationType(required, properties, json_properties) {
			this.schemaLocal.status_id = this.$store.state.moduleCommon.status.find(({name}) => name === "ACTIVE").id;
			this.schemaLocal.json_base.properties = properties;
			this.schemaLocal.json_base.required = required;
			this.schemaLocal.json_properties = json_properties;

			try {
				this.$vs.loading();
				await this.$store.dispatch("moduleTypeConfigCrud/addTypeConfig", this.schemaLocal);
				this.$vs.notify({
					color: "success",
					title: this.$t("DesignConfig.TitleSuccessTypeConfigCreated"),
					text: this.$t("DesignConfig.TextSuccessTypeConfigCreated"),
				});
				this.$vs.loading.close();
				this.$emit("consultTypeConfigEmit");
				this.cancelEdit();
			} catch (error) {
				this.$vs.loading.close();
				console.error(error);
				this.$vs.notify({
					color: "danger",
					title: this.$t("DesignConfig.TitleSuccessTypeConfigCreatedError"),
					text: this.$t("DesignConfig.TextSuccessTypeConfigCreatedError"),
				});
			}
		},
		async updateConfigurationType(required, properties, json_properties) {
			const typeConfiguration = new TypeConfiguration(this.schemaLocal);
			typeConfiguration.json_base.properties = properties;
			typeConfiguration.json_base.required = required;
			typeConfiguration.json_properties = json_properties;

			try {
				this.$vs.loading();
				await this.$store.dispatch("moduleTypeConfigCrud/updateTypeConfig", typeConfiguration);
				this.$vs.notify({
					color: "success",
					title: this.$t("DesignConfig.TitleConfirmTypeConfigUpdate"),
					text: this.$t("DesignConfig.TextConfirmTypeConfigUpdate"),
				});
				this.$vs.loading.close();
				this.$emit("consultTypeConfigEmit");
				this.cancelEdit();
			} catch (error) {
				this.$vs.loading.close();
				console.error(error);
				this.$vs.notify({
					color: "danger",
					title: this.$t("DesignConfig.TitleSuccessTypeConfigCreatedError"),
					text: this.$t("DesignConfig.TextSuccessTypeConfigCreatedError"),
				});
			}
		},
		handleError(status) {
			if (status === 401) {
				throw new BadRequestError();
			}
			if (status === 500) {
				throw new ServerError();
			}

			if (status === 722) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Roles.Permissions.NotPermissionsTitle"),
					text: `${this.$t("Roles.Permissions.NotPermissionsText")} ${error.error}`,
					time: 5000,
				});
			}
		},
		cancelEdit() {
			this.$emit("changeActiveTabEmit", 0);
		},
	},
	created() {
		if (!moduleTypeConfigCrud.isRegistered) {
			this.$store.registerModule("moduleTypeConfigCrud", moduleTypeConfigCrud);
			moduleTypeConfigCrud.isRegistered = true;
		}

		if (!moduleCommon.isRegistered) {
			this.$store.registerModule("moduleCommon", moduleCommon);
			moduleCommon.isRegistered = true;
		}

		if (!moduleSchedule.isRegistered) {
			this.$store.registerModule("moduleSchedule", moduleSchedule);
			moduleSchedule.isRegistered = true;
		}
	},
};
</script>

<style lang="scss" scoped>
.scroll-area--content-options {
	margin: auto;
	width: 100%;
	min-height: auto;
	max-height: 150px;
}
</style>
